// src/views/Seguridad/Roles/ListaRoles.jsx
import React, { useEffect, useRef, useState } from "react";

// Componentes reutilizables
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";
import IconEye from "../../../components/icons/IconEye";
import IconEdit from "../../../components/icons/IconEdit";
import IconTrash from "../../../components/icons/IconTrash";

// Componentes propios de la vista
import EditarRoles from "./EditarRoles";
import EliminarRoles from "./EliminarRoles";
import CrearRoles from "./CrearRoles";
import VerRoles from "./VerRoles";

// Hooks y utilidades
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function ListaRoles() {
  // ===== Estados =====
  const [mostrarEliminar, setMostrarEliminar] = useState(false);
  const [rolAEliminar, setRolAEliminar] = useState(null);

  const [mostrarEditor, setMostrarEditor] = useState(false);
  const [rolSel, setRolSel] = useState(null);

  const [mostrarCrear, setMostrarCrear] = useState(false);

  // Estados para el modal de visualización de permisos
  const [mostrarVerPermisos, setMostrarVerPermisos] = useState(false);
  const [rolSeleccionadoParaVer, setRolSeleccionadoParaVer] = useState(null);

  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [pagina, setPagina] = useState(1); // UI 1-based
  const [porPagina, setPorPagina] = useState(30);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);

  useEscClose(() => setMostrarEliminar(false), mostrarEliminar);
  useEscClose(() => setMostrarEditor(false), mostrarEditor);
  useEscClose(() => setMostrarCrear(false), mostrarCrear);
  useEscClose(() => setMostrarVerPermisos(false), mostrarVerPermisos);

  const firstRef = useRef(false);

  // ===== Cargar listado =====
  const cargar = async (page1 = pagina, size = porPagina) => {
    try {
      setCargando(true);
      const url = apiRoutes.roles.listUrl(page1 - 1, size);
      const resp = await appFetchJson(url);
      setItems(Array.isArray(resp?.items) ? resp.items : []);
      setTotal(Number(resp?.totalElements ?? 0));
      setTotalPaginas(Number(resp?.totalPages ?? 1));
      setPorPagina(Number(resp?.size ?? size));
      setPagina(Number((resp?.page ?? page1 - 1) + 1));
    } catch (e) {
      console.error("Error cargando roles:", e);
      setItems([]);
      setTotal(0);
      setTotalPaginas(1);
    } finally {
      setCargando(false);
    }
  };

  // Carga inicial + recarga al cambiar tamaño
  useEffect(() => {
    if (!firstRef.current) {
      firstRef.current = true;
      cargar(1, porPagina);
    } else {
      cargar(1, porPagina);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [porPagina]);

  const cambiarPagina = (num) => {
    if (num < 1 || num > totalPaginas) return;
    cargar(num, porPagina);
  };

  /* ========= FULL SCREEN: Crear ========= */
  if (mostrarCrear) {
    return (
      <CrearRoles
        onVolver={() => {
          setMostrarCrear(false);
          setPagina(1);
          cargar(1, porPagina);
        }}
        onSaved={() => {
          setMostrarCrear(false);
          setPagina(1);
          cargar(1, porPagina);
        }}
      />
    );
  }

  /* ========= COLUMNAS PARA LA TABLA ========= */
  const columns = [
    {
      header: "Nombre",
      accessor: "nombre",
      type: "text",
      width: "200px",
    },
    {
      header: "Permisos",
      accessor: "permisosCount",
      type: "text",
      width: "150px",
      render: (row) => `${row.permisosCount ?? 0} permisos`,
    },
    {
      header: "Tipo",
      type: "text",
      width: "100px",
      render: (row) => (row.staff ? "STAFF" : "CLIENTE"),
    },
    {
      header: "Acción",
      type: "action",
      width: "120px",
      render: (row) => (
        <div className="flex justify-center items-center gap-2">
          <button
            className="p-1 text-gray-500 hover:text-blue-600"
            title="Visualizar Permisos"
            onClick={() => {
              setRolSeleccionadoParaVer({ id: row.id, rol: row.nombre });
              setMostrarVerPermisos(true);
            }}
          >
            <IconEye />
          </button>
          <button
            className="p-1 text-gray-500 hover:text-emerald-600"
            title="Editar"
            onClick={() => {
              setRolSel({
                id: row.id,
                nombre: row.nombre,
                permisos: [],
              });
              setMostrarEditor(true);
            }}
          >
            <IconEdit />
          </button>
          <button
            className="p-1 text-gray-500 hover:text-red-600"
            title="Eliminar"
            onClick={() => {
              setRolAEliminar({
                id: row.id,
                nombre: row.nombre,
                asignadoA: [],
              });
              setMostrarEliminar(true);
            }}
          >
            <IconTrash />
          </button>
        </div>
      ),
    },
  ];

  /* ========= VISTA PRINCIPAL ========= */
  return (
    <div className="page">
      {/* HEADER con título, botón Agregar y selector de página */}
      <ListHeader
        title="Roles"
        showAdd
        addLabel="Agregar"
        onAddClick={() => setMostrarCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => setPorPagina(size)}
      />

      {/* Indicador de carga / error */}
      {cargando && (
        <div className="mt-4 text-sm text-center text-gray-600">
          Cargando roles…
        </div>
      )}

      {/* Tabla dinámica */}
      {!cargando && (
        <DynamicTable
          size="M" // 4 columnas
          data={items}
          columns={columns}
        />
      )}

      {/* PAGINADOR */}
      <ListFooter
        page={pagina}
        pageSize={porPagina}
        total={total}
        onPageChange={cambiarPagina}
      />

      {/* ========================================================= */}
      {/* ============ MODAL VISUALIZAR PERMISOS ================== */}
      {/* ========================================================= */}
      {mostrarVerPermisos && rolSeleccionadoParaVer != null && (
        <div
          className="modal-backdrop"
          onClick={() => setMostrarVerPermisos(false)}
        >
          <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setMostrarVerPermisos(false)}
              className="modal-close-x"
            >
              &times;
            </button>
            <VerRoles
              data={rolSeleccionadoParaVer}
              onClose={() => setMostrarVerPermisos(false)}
            />
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* =============== MODAL ELIMINAR ROL ====================== */}
      {/* ========================================================= */}
      {mostrarEliminar && rolAEliminar && (
        <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[600px] rounded-2xl shadow-xl overflow-hidden">
            <EliminarRoles
              initial={rolAEliminar}
              onClose={() => {
                setMostrarEliminar(false);
                setRolAEliminar(null);
              }}
              onDeleted={() => {
                setMostrarEliminar(false);
                setRolAEliminar(null);
                cargar(pagina, porPagina);
              }}
              onEliminar={async ({ id }) => {
                return await appFetchJson(apiRoutes.roles.delete(id), {
                  method: "DELETE",
                });
              }}
            />
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* ================= MODAL EDITAR ROL ====================== */}
      {/* ========================================================= */}
      {mostrarEditor && rolSel && (
        <div className="fixed inset-0 z-[10000] bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-[1000px] rounded-2xl shadow-xl mt-16 mb-6">
            <EditarRoles
              initial={rolSel}
              onVolver={() => {
                setMostrarEditor(false);
                setRolSel(null);
              }}
              onGuardar={() => {
                setMostrarEditor(false);
                setRolSel(null);
                cargar(pagina, porPagina);
              }}
              onEliminar={() => {
                setMostrarEditor(false);
                setRolSel(null);
                cargar(pagina, porPagina);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}